import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, Smartphone, Monitor, Share2, Plus, CheckCircle, ArrowLeft, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';

const LOGO = '/logo.png';

export default function InstallPage() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const appUrl = window.location.origin;

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) setInstalled(true);
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const r = await prompt.userChoice;
    if (r.outcome === 'accepted') setInstalled(true);
    setPrompt(null);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(appUrl);
    toast.success('Enlace copiado al portapapeles');
  };

  return (
    <div data-testid="install-page" className="min-h-screen">
      {/* Hero */}
      <div className="bg-[#0B1F40] text-white py-16 px-4 border-b-4 border-[#E50B15]">
        <div className="max-w-3xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-1 text-[10px] font-black text-white/30 hover:text-[#E50B15] tracking-[0.15em] uppercase mb-8"><ArrowLeft className="h-3 w-3"/> INICIO</Link>
          <img src={LOGO} alt="RP" className="h-20 w-20 mx-auto mb-6 border-2 border-white/20" />
          <h1 className="text-4xl sm:text-6xl tracking-tighter leading-none mb-4" style={{fontFamily:'Archivo Black'}}>
            INSTALAR<br/><span className="text-[#E50B15]">RP</span> NOTICIAS
          </h1>
          <p className="text-white/40 text-sm tracking-wider uppercase mb-8">La crítica de los hechos — en tu dispositivo</p>

          {installed ? (
            <div className="inline-flex items-center gap-2 bg-emerald-600 text-white font-black text-sm px-6 py-3">
              <CheckCircle className="h-4 w-4" /> APP INSTALADA
            </div>
          ) : prompt ? (
            <button data-testid="install-pwa-btn" onClick={install}
              className="bg-[#E50B15] text-white font-black text-sm px-8 py-3 brutal-btn-press transition-all" style={{boxShadow:'4px 4px 0px #fff'}}>
              <Download className="h-4 w-4 inline mr-2" /> INSTALAR AHORA
            </button>
          ) : (
            <a href={appUrl} data-testid="install-link-hero" className="inline-block bg-[#E50B15] text-white font-black text-sm px-8 py-3 brutal-btn-press transition-all no-underline" style={{boxShadow:'4px 4px 0px #fff'}}>
              <Download className="h-4 w-4 inline mr-2" /> ABRIR RP NOTICIAS
            </a>
          )}

          {/* Link box */}
          <div className="mt-8 max-w-md mx-auto">
            <p className="text-[10px] font-black tracking-[0.2em] uppercase text-white/30 mb-2">ENLACE DE INSTALACIÓN</p>
            <div className="flex items-center bg-white/10 border-2 border-white/20">
              <span data-testid="install-url-display" className="flex-1 px-4 py-2.5 text-xs font-bold text-white/80 truncate select-all">{appUrl}</span>
              <button data-testid="copy-install-link" onClick={copyLink} className="shrink-0 bg-[#E50B15] text-white h-full px-4 py-2.5 flex items-center gap-1.5 text-[10px] font-black hover:bg-[#E50B15]/80 transition-colors">
                <Copy className="h-3.5 w-3.5" /> COPIAR
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-xl font-black tracking-tighter text-[#0B1F40] mb-8 text-center" style={{fontFamily:'Archivo Black'}}>CÓMO INSTALAR</h2>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Android */}
          <div className="border-2 border-[#0B1F40] p-6 brutal-shadow">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-[#0B1F40]">
              <Smartphone className="h-4 w-4 text-[#E50B15]" />
              <h3 className="text-sm font-black" style={{fontFamily:'Archivo Black'}}>ANDROID</h3>
            </div>
            <ol className="space-y-3 text-xs text-[#0B1F40]/70 mb-4">
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">01.</span>Abre este enlace en <strong className="text-[#0B1F40]">Chrome</strong>:</li>
            </ol>
            <a href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="install-link-android"
              className="block w-full bg-[#0B1F40] text-white text-[10px] font-black px-4 py-2.5 mb-4 flex items-center justify-between gap-2 brutal-btn-press transition-all truncate" style={{boxShadow:'3px 3px 0px #E50B15'}}>
              <span className="truncate">{appUrl}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
            <ol start={2} className="space-y-3 text-xs text-[#0B1F40]/70">
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">02.</span>Toca <strong className="text-[#0B1F40]">&#8942;</strong> (menú de 3 puntos)</li>
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">03.</span>Selecciona <strong className="text-[#0B1F40]">"Instalar app"</strong></li>
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">04.</span>Confirma y listo</li>
            </ol>
          </div>

          {/* iOS */}
          <div className="border-2 border-[#0B1F40] p-6 brutal-shadow">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-[#0B1F40]">
              <Smartphone className="h-4 w-4 text-[#E50B15]" />
              <h3 className="text-sm font-black" style={{fontFamily:'Archivo Black'}}>iPHONE / iPAD</h3>
            </div>
            <ol className="space-y-3 text-xs text-[#0B1F40]/70 mb-4">
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">01.</span>Abre este enlace en <strong className="text-[#0B1F40]">Safari</strong>:</li>
            </ol>
            <a href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="install-link-ios"
              className="block w-full bg-[#0B1F40] text-white text-[10px] font-black px-4 py-2.5 mb-4 flex items-center justify-between gap-2 brutal-btn-press transition-all truncate" style={{boxShadow:'3px 3px 0px #E50B15'}}>
              <span className="truncate">{appUrl}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
            <ol start={2} className="space-y-3 text-xs text-[#0B1F40]/70">
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">02.</span>Toca <strong className="text-[#0B1F40]">Compartir</strong> <Share2 className="h-3 w-3 inline"/></li>
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">03.</span><strong className="text-[#0B1F40]">"Agregar a inicio"</strong> <Plus className="h-3 w-3 inline"/></li>
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">04.</span>Toca <strong className="text-[#0B1F40]">"Agregar"</strong></li>
            </ol>
          </div>

          {/* Desktop */}
          <div className="border-2 border-[#0B1F40] p-6 brutal-shadow md:col-span-2">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-[#0B1F40]">
              <Monitor className="h-4 w-4 text-[#E50B15]" />
              <h3 className="text-sm font-black" style={{fontFamily:'Archivo Black'}}>COMPUTADORA</h3>
            </div>
            <ol className="space-y-3 text-xs text-[#0B1F40]/70 mb-4">
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">01.</span>Abre este enlace en <strong className="text-[#0B1F40]">Chrome o Edge</strong>:</li>
            </ol>
            <a href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="install-link-desktop"
              className="block w-full bg-[#0B1F40] text-white text-[10px] font-black px-4 py-2.5 mb-4 flex items-center justify-between gap-2 brutal-btn-press transition-all" style={{boxShadow:'3px 3px 0px #E50B15'}}>
              <span className="truncate">{appUrl}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
            <ol start={2} className="space-y-3 text-xs text-[#0B1F40]/70">
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">02.</span>Clic en el ícono <strong className="text-[#0B1F40]">de instalación</strong> <Download className="h-3 w-3 inline"/> en la barra de direcciones</li>
              <li className="flex gap-2"><span className="font-black text-[#E50B15] shrink-0">03.</span>Confirma y se instala como app de escritorio</li>
            </ol>
          </div>
        </div>

        {/* Features */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[{i:'ACCESO DIRECTO'},{i:'PANTALLA COMPLETA'},{i:'CARGA RÁPIDA'},{i:'FUNCIONA OFFLINE'}].map(f=>(
            <div key={f.i} className="border-2 border-[#0B1F40]/10 p-4 text-center hover:border-[#0B1F40] transition-colors">
              <p className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">{f.i}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
